import { t } from 'i18next';

import { type Option } from '~/components/SmartSelect';
import { carrierHashMap } from '~/constants/carriers';

import type Carrier from './Carrier';
import type CarrierAccountRegStatus from './CarrierAccountRegStatus';

// JSON request/response format for /carrier_accounts
// Also JSON response format for /carrier_accounts/register/new endpoint
interface JSONCarrierAccount {
  account_id: string;
  active?: boolean;
  carrier: string;
  id?: string;
  is_shippo_account?: boolean;
  metadata?: string;
  object_id?: string;
  object_info?: CarrierObjectInfo;
  object_owner?: string;
  parameters?: CleanedParameters;
  test?: boolean;
}

interface CarrierObjectInfo {
  authentication: {
    status?: 'authorization_pending' | 'connected' | 'disconnected';
    type: 'default' | 'oauth';
  };
  verification?: {
    policy?: 'not-required' | 'required';
  };
}

// JSON request format for /carrier_accounts/register/new endpoint
interface JSONCreateCarrierAccount {
  carrier: string;
  parameters: CleanedParameters;
}

// CarrierAccount model
interface CarrierAccount {
  accountId: string;
  active?: boolean;
  carrier: Carrier;
  id?: string;
  isShippoAccount?: boolean;
  metadata?: string;
  objectId?: string;
  objectInfo?: CarrierObjectInfo;
  objectOwner?: string;
  parameters?: Parameters;
  regStatus?: CarrierAccountRegStatus;
  test?: boolean;
}

type Parameters = { [key: string]: Option | boolean | number | string };
type CleanedParameters = { [key: string]: boolean | number | string };

type CarrierServiceLevelsEditPayload = { op: string; path: string; value: boolean }[];

// Returns true if obj is of Option type
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isOption = (obj: any): obj is Option => obj?.value !== undefined;

// Converts a CarrierAccount object to the JSON request format used for /carrier_accounts/register/new endpoint
const toCreateJSON = (carrierAccount: CarrierAccount): JSONCreateCarrierAccount => {
  const jsonCreateCarrierAccount: JSONCreateCarrierAccount = {
    carrier: carrierAccount.carrier.id,
    parameters: {},
  };

  if ('parameters' in carrierAccount) {
    const parameters = carrierAccount.parameters;

    if (parameters) jsonCreateCarrierAccount.parameters = cleanParameters(parameters);
  }

  return jsonCreateCarrierAccount;
};

// Converts a CarrierAccount object to the JSON request format used for /carrier_accounts endpoint
const toJSON = (carrierAccount: CarrierAccount): JSONCarrierAccount => {
  const jsonCarrierAccount: JSONCarrierAccount = {
    account_id: carrierAccount.accountId,
    carrier: carrierAccount.carrier.id,
  };

  if ('objectId' in carrierAccount) jsonCarrierAccount.object_id = carrierAccount.objectId;

  if ('objectOwner' in carrierAccount) jsonCarrierAccount.object_owner = carrierAccount.objectOwner;

  if ('parameters' in carrierAccount) {
    const parameters = carrierAccount.parameters;

    if (parameters) jsonCarrierAccount.parameters = cleanParameters(parameters);
  }

  if ('test' in carrierAccount) jsonCarrierAccount.test = carrierAccount.test;

  if ('active' in carrierAccount) jsonCarrierAccount.active = carrierAccount.active;

  if ('isShippoAccount' in carrierAccount)
    jsonCarrierAccount.is_shippo_account = carrierAccount.isShippoAccount;

  if ('metadata' in carrierAccount) jsonCarrierAccount.metadata = carrierAccount.metadata;

  return jsonCarrierAccount;
};

// Converts a JSON response from /carrier_accounts or /carrier_accounts/register/new endpoint to CarrierAccount object
const fromJSON = (jsonCarrierAccount: JSONCarrierAccount): CarrierAccount => {
  const serialized: CarrierAccount = {
    accountId: jsonCarrierAccount.account_id,
    carrier: carrierHashMap[jsonCarrierAccount.carrier],
    id: jsonCarrierAccount.id,
  };

  if ('object_id' in jsonCarrierAccount) serialized.objectId = jsonCarrierAccount.object_id;

  if ('object_owner' in jsonCarrierAccount)
    serialized.objectOwner = jsonCarrierAccount.object_owner;

  if ('object_info' in jsonCarrierAccount) serialized.objectInfo = jsonCarrierAccount.object_info;

  if (jsonCarrierAccount.carrier && jsonCarrierAccount.carrier in carrierHashMap)
    serialized.carrier = carrierHashMap[jsonCarrierAccount.carrier];

  if ('parameters' in jsonCarrierAccount) serialized.parameters = jsonCarrierAccount.parameters;

  if ('test' in jsonCarrierAccount) serialized.test = jsonCarrierAccount.test;

  if ('active' in jsonCarrierAccount) serialized.active = jsonCarrierAccount.active;

  if ('is_shippo_account' in jsonCarrierAccount)
    serialized.isShippoAccount = jsonCarrierAccount.is_shippo_account;

  if ('metadata' in jsonCarrierAccount) serialized.metadata = jsonCarrierAccount.metadata;

  return serialized;
};

// Helper Functions

/*
  Description:
    Takes Parameters (dict of key-value pairs, where value can be string/boolean/number/Option)
    Scans Paramters, and if an Option is found, cleans the parameter to use the Option's value.
    The passed in Parameters object is not modified, the returned object is a new CleanedParameters
    object.

  Parameters:
    - parameters: Parameters, dict of key-value pairs, where value can be string/boolean/number/Option

  Returns: CleanedParameters object
*/
const cleanParameters = (parameters: Parameters): CleanedParameters => {
  const cleaned: CleanedParameters = {};

  // Extract value out of Option parameters
  for (const key of Object.keys(parameters)) {
    cleaned[key] = isOption(parameters[key])
      ? (parameters[key] as Option)['value']
      : (parameters[key] as boolean | number | string);
  }

  return cleaned;
};

// function to format keys used to display multiple carrier accounts in batch rates
const formatServiceLevelKey = (carrierAccountId: string, token: string, isTest = false): string =>
  `${carrierAccountId}__${token}${isTest ? '__test' : ''}`;

const getCarrierAccountLabel = (carrierAccount: CarrierAccount) => {
  let caLabel = '';
  if (carrierAccount && carrierAccount.objectId) {
    if (carrierAccount.isShippoAccount) {
      caLabel = t('orders:calculateRates.rates.serviceLevels.accountType.master');
    } else {
      caLabel =
        carrierAccount.metadata ||
        t('orders:calculateRates.rates.serviceLevels.accountType.personal');
    }
  }
  return caLabel;
};

export {
  CarrierAccount,
  CarrierServiceLevelsEditPayload,
  JSONCarrierAccount,
  JSONCreateCarrierAccount,
  formatServiceLevelKey,
  fromJSON,
  getCarrierAccountLabel,
  toCreateJSON,
  toJSON,
};

export default CarrierAccount;
